import React, { useState, useEffect, useMemo } from 'react';
import { Menu, List, Grid, MenuItem, useTheme, Button, Badge } from '@mui/material';
import { Whatshot, FiberManualRecord, AccountTreeRounded } from '@mui/icons-material';
import { NotInterested, AccessTimeFilledRounded, FilterAltRounded } from '@mui/icons-material';
import { CheckBoxItem, ClearFilter } from '../../../Global/Components';
import { FormIcon, ProcessIcon } from '../../../Global/Icons';
import { CheckboxWithIcon, CheckboxWithUser, UserAvatar } from '../../../Global/Components';
import TaskTypes from '../../../Global/TaskTypes';
import useMediaQuery from '@mui/material/useMediaQuery';
import { grey } from '@mui/material/colors';
import { isArray, isEmpty } from 'lodash';
import { v4 } from 'uuid';
import { convertToTitleCase, dueDateFilters, filterFields, priorities, priorityColors } from '../constants';
import DataGridViews from '../../../Global/DataGrid/Views';

const getUpdatedValue = ({ fieldName, oldFilter, value, newValue }) => {
  if (fieldName === filterFields.title) return value;
  else return newValue ? [...oldFilter, value] : oldFilter?.filter((x) => x !== value);
};

export default function TaskFilters({
  selectedFilters,
  setSelectedFilters,
  searchTerm,
  endItems,
  taskInfo,
  startItems,
  pageType,
  includeRefColumns,
  gridConfig,
  defaultView,
  configId,
  onConfigChange,
  views,
  forHomePage = false,
}) {
  const [showFilters, setShowFilters] = useState(false);

  //menus
  const [priorityMenu, setPriorityMenu] = useState(null);
  const [typeMenu, setTypeMenu] = useState(null);
  const [dueMenu, setDueMenu] = useState(null);
  const [statusMenu, setStatusMenu] = useState(null);
  const [ownerFilterMenu, setOwnerFilterMenu] = useState(null);
  const [assigneeFilterMenu, setAssigneeFilterMenu] = useState(null);
  const [formMenu, setFormMenu] = useState(null);
  const [processMenu, setProcessMenu] = useState(null);

  const theme = useTheme();
  const largeDevices = useMediaQuery(theme.breakpoints.up('sm'));

  const {
    priorityFilter = [],
    typeFilter = [],
    dueFilter = [],
    statusFilter = [],
    ownerFilter = [],
    assigneeFilter = [],
    formFilter = [],
    processFilter = [],
  } = useMemo(() => {
    if (!isArray(selectedFilters?.items)) return {};

    return selectedFilters.items.reduce((group, filter) => {
      switch (filter.field) {
        case filterFields.priority:
          group['priorityFilter'] = filter.value;
          break;
        case filterFields.type:
          group['typeFilter'] = filter.value;
          break;
        case filterFields.dueAt:
          group['dueFilter'] = filter.key;
          break;
        case 'statusId':
          group['statusFilter'] = filter.value;
          break;
        case 'created_by':
          group['ownerFilter'] = filter.value;
          break;
        case 'assignedUsers':
          group['assigneeFilter'] = filter.value;
          break;
        case 'form':
          group['formFilter'] = filter.value;
          break;
        case 'process':
          group['processFilter'] = filter.value;
          break;
      }
      return group;
    }, {});
  }, [selectedFilters]);

  const { statusColors, taskTypes, forms, processes } = useMemo(() => {
    if (!isArray(taskInfo?.statusGroups)) return { statusColors: {}, taskTypes: {} };

    const groups = taskInfo.statusGroups
      .flatMap((x) => x.statuses)
      .reduce((prev, curr) => ({ ...prev, [curr.id]: curr.color }), {});

    const newTaskTypes = {};
    Object.keys(TaskTypes).forEach((taskType) => {
      if (taskInfo.types.includes(taskType)) {
        newTaskTypes[taskType] = TaskTypes[taskType];
      }
    });

    return { statusColors: groups, taskTypes: newTaskTypes, forms: taskInfo.forms, processes: taskInfo.processes };
  }, [taskInfo]);

  const anyFilterSelected =
    priorityFilter?.length ||
    typeFilter?.length ||
    dueFilter?.length ||
    statusFilter?.length ||
    ownerFilter.length ||
    false;

  const handleFilterChange = ({ value, newValue, fieldName, oldFilter }) => {
    const updated = getUpdatedValue({ fieldName, oldFilter, value, newValue });
    const filterItems = [...(selectedFilters?.items || [])];
    const index = filterItems.findIndex((item) => item.field === fieldName);

    if (index > -1) {
      const filterItem = filterItems.splice(index, 1)[0];

      if (updated?.length) {
        filterItems.push({ ...filterItem, value: updated });
      }
    } else {
      filterItems.push({
        field: fieldName,
        operator: fieldName === filterFields.title ? 'contains' : 'isAnyOf',
        id: v4(),
        value: updated,
      });
    }
    setSelectedFilters({ items: filterItems });
  };

  const handleDueDateFilterChange = (key) => {
    const filterItems = [...(selectedFilters?.items || [])];
    const index = filterItems.findIndex((item) => item.field === filterFields.dueAt);

    const { operator, value } = dueDateFilters[key];

    if (index > -1) {
      const filterItem = filterItems.splice(index, 1)[0];

      if (key) {
        filterItems.push({ ...filterItem, operator, value, key });
      }
    } else {
      filterItems.push({ field: filterFields.dueAt, operator, value, key });
    }
    setSelectedFilters({ items: filterItems });
  };

  const clearFilter = ({ fieldName }) => {
    const filterItems = [...(selectedFilters?.items || [])];
    const index = filterItems.findIndex((item) => item.field === fieldName);
    if (index > -1) {
      filterItems.splice(index, 1);
      setSelectedFilters({ items: filterItems });
    }
  };

  useEffect(() => {
    if (searchTerm == null) return;
    handleFilterChange({ value: searchTerm, newValue: searchTerm, fieldName: filterFields.title });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [searchTerm]);

  function getButtonProps(active) {
    return {
      startIcon: (
        <Badge invisible={!active} variant="dot" color="error">
          <FilterAltRounded />
        </Badge>
      ),
      sx: {
        textTransform: 'none',
        color: (theme) => theme.palette.text.secondary,
        fontWeight: 400,
      },
      variant: 'text',
    };
  }

  return (
    <>
      {!largeDevices && (
        <Grid item xs={6} sx={{ mb: 1 }}>
          <Button startIcon={<FilterAltRounded />} onClick={() => setShowFilters(!showFilters)}>
            Filters
          </Button>
        </Grid>
      )}

      {(largeDevices || showFilters) && (
        <Grid item container alignItems="center" spacing={1}>
          {startItems}
          <Grid sx={{ ml: 'auto' }} />
          {/* ASSIGNEE FILTER */}
          {['assigned', 'watching', 'team'].includes(pageType) &&
            isArray(taskInfo?.assignees) &&
            taskInfo.assignees.length > 1 && (
              <Grid item>
                <Button
                  {...getButtonProps(!isEmpty(assigneeFilter))}
                  onClick={(e) => setAssigneeFilterMenu(e.currentTarget)}
                >
                  {assigneeFilter.map((x, i) => (
                    <UserAvatar key={i} userId={x} sx={{ mr: 1 }} />
                  ))}
                  Assignee
                </Button>

                <Menu
                  anchorEl={assigneeFilterMenu}
                  open={Boolean(assigneeFilterMenu)}
                  onClose={() => setAssigneeFilterMenu(null)}
                >
                  {taskInfo.assignees.map((assignees, i) => (
                    <CheckboxWithUser
                      key={i}
                      checked={assigneeFilter.includes(assignees)}
                      onChange={(newValue) => {
                        handleFilterChange({
                          value: assignees,
                          newValue,
                          fieldName: 'assignedUsers',
                          oldFilter: assigneeFilter,
                        });
                      }}
                      userId={assignees}
                    />
                  ))}
                  <ClearFilter
                    onClick={() => {
                      clearFilter({ fieldName: 'assignedUsers' });
                      setAssigneeFilterMenu(null);
                    }}
                  />
                </Menu>
              </Grid>
            )}

          {/* CREATOR FILTER */}
          {['my', 'watching', 'team'].includes(pageType) && isArray(taskInfo?.owners) && taskInfo.owners.length > 1 && (
            <Grid item>
              <Button {...getButtonProps(!isEmpty(ownerFilter))} onClick={(e) => setOwnerFilterMenu(e.currentTarget)}>
                {ownerFilter.map((x, i) => (
                  <UserAvatar key={i} userId={x} sx={{ mr: 1 }} />
                ))}
                Owner
              </Button>

              <Menu anchorEl={ownerFilterMenu} open={Boolean(ownerFilterMenu)} onClose={() => setOwnerFilterMenu(null)}>
                {taskInfo.owners.map((owner, i) => (
                  <CheckboxWithUser
                    key={i}
                    checked={ownerFilter.includes(owner)}
                    onChange={(newValue) => {
                      handleFilterChange({
                        value: owner,
                        newValue,
                        fieldName: 'created_by',
                        oldFilter: ownerFilter,
                      });
                    }}
                    userId={owner}
                  />
                ))}
                <ClearFilter
                  onClick={() => {
                    clearFilter({ fieldName: 'created_by' });
                    setOwnerFilterMenu(null);
                  }}
                />
              </Menu>
            </Grid>
          )}
          {/* PRIORITY FILTER */}
          <Grid item>
            <Button
              {...getButtonProps(!isEmpty(priorityFilter))}
              onClick={(event) => setPriorityMenu(event.currentTarget)}
            >
              {priorityFilter.map((priority) => (
                <Whatshot key={priority} sx={{ color: priorityColors[priority], mr: 1 }} />
              ))}
              Priority
            </Button>

            <Menu
              anchorEl={priorityMenu}
              keepMounted
              open={Boolean(priorityMenu)}
              onClose={() => setPriorityMenu(null)}
            >
              {priorities.map((priority) => (
                <CheckboxWithIcon
                  key={priority}
                  checked={priorityFilter?.includes(priority)}
                  onChange={(newValue) => {
                    handleFilterChange({
                      value: priority,
                      newValue,
                      fieldName: filterFields.priority,
                      oldFilter: priorityFilter,
                    });
                  }}
                  text={priority}
                  icon={<Whatshot style={{ color: priorityColors[priority] }} />}
                />
              ))}
              <ClearFilter
                onClick={() => {
                  clearFilter({ fieldName: filterFields.priority });
                  setPriorityMenu(null);
                }}
              />
            </Menu>
          </Grid>
          {/* TYPE FILTER */}
          {!isEmpty(taskTypes) && (
            <Grid item>
              <Button {...getButtonProps(!isEmpty(typeFilter))} onClick={(event) => setTypeMenu(event.currentTarget)}>
                {typeFilter.map((type) => {
                  const selectedType = Object.keys(taskTypes).find((task) => task === type);
                  const icon = taskTypes[selectedType]?.icon;
                  return (
                    <Grid key={type} style={{ marginRight: 10, display: 'flex' }}>
                      {icon}
                    </Grid>
                  );
                })}
                Type
              </Button>
              <Menu anchorEl={typeMenu} open={Boolean(typeMenu)} onClose={() => setTypeMenu(null)}>
                {Object.keys(taskTypes).map((type) => (
                  <CheckboxWithIcon
                    key={type}
                    checked={typeFilter?.includes(type)}
                    onChange={(newValue) => {
                      handleFilterChange({
                        value: type,
                        newValue,
                        fieldName: filterFields.type,
                        oldFilter: typeFilter,
                      });
                    }}
                    text={taskTypes[type]?.text}
                    icon={taskTypes[type]?.icon}
                  />
                ))}
                <ClearFilter
                  onClick={() => {
                    clearFilter({ fieldName: filterFields.type });
                    setTypeMenu(null);
                  }}
                />
              </Menu>
            </Grid>
          )}

          {/* DUE FILTER */}
          <Grid item>
            <Button {...getButtonProps(!isEmpty(dueFilter))} onClick={(e) => setDueMenu(e.currentTarget)}>
              {!isEmpty(dueFilter) && <AccessTimeFilledRounded sx={{ mr: 1 }} />}
              Due
            </Button>
            <Menu anchorEl={dueMenu} open={Boolean(dueMenu)} onClose={() => setDueMenu(null)}>
              {Object.keys(dueDateFilters).map((due) => (
                <CheckBoxItem
                  key={due}
                  text={convertToTitleCase(due)}
                  checked={dueFilter.includes(due)}
                  onChange={() => {
                    handleDueDateFilterChange(due);
                  }}
                />
              ))}
              <ClearFilter
                onClick={() => {
                  clearFilter({ fieldName: filterFields.dueAt });
                  setDueMenu(null);
                }}
              />
            </Menu>
          </Grid>
          {/* STATUS FILTER */}
          <Grid item>
            <Button {...getButtonProps(!isEmpty(statusFilter))} onClick={(event) => setStatusMenu(event.currentTarget)}>
              {statusFilter.map((x) => (
                <FiberManualRecord key={x} sx={{ color: statusColors[x] || grey[500], mr: 1 }} />
              ))}
              Status
            </Button>

            {isArray(taskInfo?.statusGroups) && !isEmpty(taskInfo?.statusGroups) && (
              <Menu anchorEl={statusMenu} open={Boolean(statusMenu)} onClose={() => setStatusMenu(null)}>
                {taskInfo?.statusGroups.map((group, i) => (
                  <List key={i}>
                    {group.projectId && (
                      <MenuItem sx={{ fontSize: '0.9rem', fontWeight: 500 }}>
                        <AccountTreeRounded fontSize="small" sx={{ mr: 1.5, color: grey[500] }} />
                        {group.projectTitle}
                      </MenuItem>
                    )}
                    {group.statuses.map((status, j) => (
                      <CheckboxWithIcon
                        key={j}
                        checked={statusFilter.includes(status.id)}
                        onChange={(newValue) => {
                          handleFilterChange({
                            value: status.id,
                            newValue,
                            fieldName: 'statusId',
                            oldFilter: statusFilter,
                          });
                        }}
                        text={status.text}
                        icon={<FiberManualRecord sx={{ color: status.color || grey[500] }} />}
                      />
                    ))}
                  </List>
                ))}
                <ClearFilter
                  onClick={() => {
                    clearFilter({ fieldName: 'statusId' });
                    setStatusMenu(null);
                  }}
                />
              </Menu>
            )}
          </Grid>
          {/* REFERENCE COLUMNS */}
          {includeRefColumns && (
            <>
              {!isEmpty(forms) && (
                <Grid item>
                  <Button
                    {...getButtonProps(!isEmpty(formFilter))}
                    onClick={(event) => setFormMenu(event.currentTarget)}
                  >
                    Form
                  </Button>

                  <Menu anchorEl={formMenu} open={Boolean(formMenu)} onClose={() => setFormMenu(null)}>
                    {forms.map((form) => (
                      <CheckboxWithIcon
                        key={form._id}
                        checked={formFilter?.includes(form._id)}
                        onChange={(newValue) => {
                          handleFilterChange({
                            value: form._id,
                            newValue,
                            fieldName: 'form',
                            oldFilter: formFilter,
                          });
                        }}
                        text={form.title}
                        icon={<FormIcon />}
                      />
                    ))}
                    <ClearFilter
                      onClick={() => {
                        clearFilter({ fieldName: 'form' });
                        setFormMenu(null);
                      }}
                    />
                  </Menu>
                </Grid>
              )}

              {!isEmpty(processes) && (
                <Grid item>
                  <Button
                    {...getButtonProps(!isEmpty(processFilter))}
                    onClick={(event) => setProcessMenu(event.currentTarget)}
                  >
                    Process
                  </Button>

                  <Menu anchorEl={processMenu} open={Boolean(processMenu)} onClose={() => setProcessMenu(null)}>
                    {processes.map((process) => (
                      <CheckboxWithIcon
                        key={process._id}
                        checked={processFilter?.includes(process._id)}
                        onChange={(newValue) => {
                          handleFilterChange({
                            value: process._id,
                            newValue,
                            fieldName: 'process',
                            oldFilter: processFilter,
                          });
                        }}
                        text={process.title}
                        icon={<ProcessIcon />}
                      />
                    ))}
                    <ClearFilter
                      onClick={() => {
                        clearFilter({ fieldName: 'process' });
                        setProcessMenu(null);
                      }}
                    />
                  </Menu>
                </Grid>
              )}
            </>
          )}
          {anyFilterSelected && (
            <Grid item>
              <Button
                startIcon={<NotInterested />}
                onClick={() => {
                  setSelectedFilters({ items: [] });
                }}
                color="error"
              >
                Clear filters
              </Button>
            </Grid>
          )}
          {endItems}
          <Grid item>
            {!forHomePage && (
              <DataGridViews
                initial={gridConfig}
                onResult={onConfigChange}
                id={configId}
                defaultId={defaultView}
                initialViews={views}
                privateOnly
              />
            )}
          </Grid>
        </Grid>
      )}
    </>
  );
}
